import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  extractCrmError,
  getCustomerOrderEventLabel,
  getCustomerOrderSourceLabel,
  getCustomerOrderStatusLabel,
  getOnecSyncStatusLabel,
  userHasCrmAccess,
  userCanEditCrm,
  CUSTOMER_ORDER_MANAGER_ATTENTION_STATUSES,
  CUSTOMER_ORDER_SOURCE_OPTIONS,
  CUSTOMER_ORDER_STATUS_COUNTERS,
  CUSTOMER_ORDER_STATUS_OPTIONS,
} from "@/lib/crmApi";
import { batchAddToSupplierCart } from "@/lib/supplierCartApi";
import CustomerOrderFormDialog from "@/components/customer-orders/CustomerOrderFormDialog";
import CustomerOrderPaymentsPanel from "@/components/customer-orders/CustomerOrderPaymentsPanel";
import { formatCrmDate, formatCrmDateTime } from "@/utils/crmDateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  Clock3,
  Filter,
  Package,
  Plus,
  RefreshCcw,
  Search,
  CircleDollarSign,
  History,
  Link2,
  Send,
  ShoppingCart,
  Printer,
  FileDown,
  Copy,
} from "lucide-react";

const STATUS_BADGE_CLASSES = {
  draft: "border-slate-200 bg-slate-50 text-slate-700",
  sent: "border-indigo-200 bg-indigo-50 text-indigo-700",
  viewed: "border-sky-200 bg-sky-50 text-sky-700",
  awaiting_confirmation: "border-amber-200 bg-amber-50 text-amber-700",
  confirmed: "border-blue-200 bg-blue-50 text-blue-700",
  awaiting_payment: "border-violet-200 bg-violet-50 text-violet-700",
  in_work: "border-orange-200 bg-orange-50 text-orange-700",
  ordered_from_supplier: "border-purple-200 bg-purple-50 text-purple-700",
  partially_arrived: "border-amber-300 bg-amber-100 text-amber-900 font-semibold",
  received: "border-teal-200 bg-teal-50 text-teal-700",
  ready: "border-cyan-200 bg-cyan-50 text-cyan-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-red-200 bg-red-50 text-red-700",
  declined: "border-rose-200 bg-rose-50 text-rose-700",
  needs_revision: "border-violet-200 bg-violet-50 text-violet-700",
};

const ACTOR_LABELS = {
  manager: "Менеджер",
  client: "Клиент",
  system: "Система",
};

const FIELD_LABELS = {
  source_message_id: "сообщение",
  source_chat_id: "чат",
  source: "источник",
  status: "статус",
  discount_percent: "скидка",
  promised_delivery_date: "дата поставки",
  currency: "валюта",
  client_name_snapshot: "имя клиента",
  phone_snapshot: "телефон",
  whatsapp_chat_id_snapshot: "чат WhatsApp",
  manager_comment: "комментарий менеджера",
  customer_comment: "комментарий клиента",
  created_by_user_id: "пользователь",
  product_id: "товар",
  product_code: "артикул",
  product_name: "наименование",
  quantity: "количество",
  price: "цена",
  discount_percent_item: "скидка",
  sort_order: "порядок",
  supplier_price_snapshot: "закупочная цена",
  supplier_id_snapshot: "поставщик",
  comment: "комментарий",
};

function formatMoney(value, currency = "KZT") {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  const formatted = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
  return `${formatted} ${currency}`.trim();
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function cleanupTableCellText(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\t/g, " ");
}

function format1cNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "";
  }
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function buildCustomerOrder1cClipboard(items) {
  const rows = (items || [])
    .map((item) => ({
      articul: cleanupTableCellText(item.product_code || item.product_id || ""),
      nomenclature: cleanupTableCellText(item.product_name || ""),
      quantity: cleanupTableCellText(String(item.quantity ?? "")),
      price: cleanupTableCellText(format1cNumber(item.price)),
    }))
    .filter((row) => row.nomenclature && row.articul)
    .sort((left, right) => {
      const leftName = left.nomenclature.replace(/[Ёё]/g, (char) => (char === "Ё" ? "Е" : "е")).toLowerCase();
      const rightName = right.nomenclature.replace(/[Ёё]/g, (char) => (char === "Ё" ? "Е" : "е")).toLowerCase();
      return (
        leftName.localeCompare(rightName, "ru") ||
        left.articul.toLowerCase().localeCompare(right.articul.toLowerCase(), "ru")
      );
    });

  return rows
    .map((row) => [row.articul, row.nomenclature, row.quantity, row.price].join("\t"))
    .join("\n");
}

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const fallbackTextarea = document.createElement("textarea");
    fallbackTextarea.value = text;
    fallbackTextarea.setAttribute("readonly", "");
    fallbackTextarea.style.position = "fixed";
    fallbackTextarea.style.opacity = "0";
    document.body.appendChild(fallbackTextarea);
    fallbackTextarea.select();
    document.execCommand("copy");
    document.body.removeChild(fallbackTextarea);
  }
}

function buildVehicleLabel(vehicle) {
  if (!vehicle) return null;
  if (typeof vehicle === "string") return vehicle.trim() || null;
  const parts = [
    vehicle.brand,
    vehicle.model,
    vehicle.generation,
    vehicle.body,
    vehicle.engine,
    vehicle.year,
  ]
    .map((item) => (item === null || item === undefined ? "" : String(item).trim()))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : null;
}

function getOrderVehicleLabel(order) {
  return (
    buildVehicleLabel(order?.vehicle) ||
    buildVehicleLabel(order?.client_vehicle) ||
    buildVehicleLabel(order?.primary_vehicle) ||
    buildVehicleLabel(order?.client?.primary_vehicle) ||
    buildVehicleLabel(order?.client?.vehicles?.find?.((item) => item?.is_primary)) ||
    "—"
  );
}

function getOrderBadgeClass(status) {
  return STATUS_BADGE_CLASSES[status] || "border-slate-200 bg-slate-50 text-slate-700";
}

function getActorLabel(actorType) {
  return ACTOR_LABELS[actorType] || actorType || "Система";
}

function fieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

function describeChanges(changes) {
  if (!changes || typeof changes !== "object") return "Поля заказа изменены";
  const fields = Object.keys(changes);
  if (!fields.length) return "Поля заказа изменены";
  return `Изменены поля: ${fields.map((field) => fieldLabel(field)).join(", ")}`;
}

function getEventDescription(event) {
  const payload = event?.payload_json || {};
  switch (event?.event_type) {
    case "created":
      return payload.order_number
        ? `Создан заказ ${payload.order_number}`
        : "Создан заказ";
    case "updated":
      if (payload.action === "prices_refreshed_from_catalog") {
        const refreshedCount = Array.isArray(payload.items_refreshed)
          ? payload.items_refreshed.length
          : Array.isArray(payload.items_updated)
            ? payload.items_updated.length
            : 0;
        const changedCount = Array.isArray(payload.items_price_changed)
          ? payload.items_price_changed.length
          : refreshedCount;
        const skippedCount = Array.isArray(payload.items_skipped) ? payload.items_skipped.length : 0;
        const parts = [];
        if (changedCount > 0) {
          parts.push(`Цены обновлены по прайсу (${changedCount} поз.)`);
        } else {
          parts.push(`Прайс проверен, цены без изменений (${refreshedCount} поз.)`);
        }
        if (payload.offer_reset) {
          parts.push("срок предложения обновлён");
        }
        if (skippedCount > 0) {
          parts.push(`не найдено в прайсе: ${skippedCount}`);
        }
        return parts.join(" · ");
      }
      return describeChanges(payload.changes);
    case "status_changed":
      return `Статус: ${getCustomerOrderStatusLabel(payload.from)} → ${getCustomerOrderStatusLabel(payload.to)}`;
    case "item_added":
      return `Добавлена позиция ${payload.item?.product_name || payload.item?.product_code || ""}`.trim();
    case "item_updated":
      return `Позиция изменена ${payload.after?.product_name || payload.after?.product_code || ""}`.trim();
    case "item_deleted":
      return `Позиция удалена ${payload.item?.product_name || payload.item?.product_code || ""}`.trim();
    case "viewed":
      return payload.to_status && payload.from_status !== payload.to_status
        ? `Клиент открыл заказ · ${getCustomerOrderStatusLabel(payload.from_status)} → ${getCustomerOrderStatusLabel(payload.to_status)}`
        : "Клиент открыл заказ";
    case "confirmed_by_client":
      return "Клиент подтвердил заказ";
    case "declined_by_client":
      return payload.comment ? `Клиент отказался: ${payload.comment}` : "Клиент отказался от заказа";
    case "price_requested_by_client":
      return payload.comment
        ? `Клиент запросил актуальную цену: ${payload.comment}`
        : "Клиент запросил актуальную цену";
    case "payment_created":
      return payload.amount
        ? `Оплата ${payload.amount.toLocaleString("ru-RU")} ₸`
        : "Оплата создана";
    case "payment_cancelled":
    case "payment_refunded":
      return payload.amount
        ? `${payload.amount.toLocaleString("ru-RU")} ₸`
        : getCustomerOrderEventLabel(event?.event_type);
    case "order_fully_paid":
      return payload.paid_amount
        ? `Полная оплата: ${payload.paid_amount.toLocaleString("ru-RU")} ₸`
        : "Заказ полностью оплачен";
    default:
      return getCustomerOrderEventLabel(event?.event_type);
  }
}

function isWithinRange(value, from, to) {
  if (!value) return false;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return false;
  if (from) {
    const min = new Date(`${from}T00:00:00`);
    if (dt < min) return false;
  }
  if (to) {
    const max = new Date(`${to}T23:59:59.999`);
    if (dt > max) return false;
  }
  return true;
}

function matchesSearch(order, q) {
  if (!q) return true;
  const haystack = [
    order.order_number,
    order.client_name_snapshot,
    order.phone_snapshot,
    order.whatsapp_chat_id_snapshot,
    order.manager_comment,
    order.customer_comment,
    order.client?.display_name,
    order.client?.manual_name,
    order.client?.phone,
    order.client?.phone_normalized,
    order.client?.effective_name,
  ]
    .filter(Boolean)
    .map(normalizeText)
    .join(" ");
  return haystack.includes(normalizeText(q));
}

function CounterCard({ label, value, active, attention, onClick }) {
  const hasAttention = attention && value > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
        active
          ? hasAttention
            ? "border-amber-500 ring-2 ring-amber-500/20"
            : "border-[#3566f6] ring-2 ring-[#3566f6]/10"
          : hasAttention
            ? "border-amber-300 bg-amber-50/60"
            : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
              hasAttention ? "text-amber-700" : "text-slate-500"
            }`}
          >
            {label}
          </p>
          <p
            className={`mt-2 text-3xl font-semibold tracking-tight ${
              hasAttention ? "text-amber-900" : "text-slate-950"
            }`}
          >
            {value}
          </p>
        </div>
        <div
          className={`mt-1 h-3 w-3 rounded-full ${
            active
              ? hasAttention
                ? "bg-amber-500"
                : "bg-[#3566f6]"
              : hasAttention
                ? "bg-amber-400 animate-pulse"
                : "bg-slate-200"
          }`}
        />
      </div>
    </button>
  );
}

function FilterLabel({ children }) {
  return <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{children}</p>;
}

function DetailLoading() {
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
      <Skeleton className="h-5 w-40 rounded-full" />
      <Skeleton className="h-28 w-full rounded-xl" />
      <div className="grid gap-3 md:grid-cols-3">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
      <Skeleton className="h-56 w-full rounded-xl" />
    </div>
  );
}

function EventCard({ event }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
              {getCustomerOrderEventLabel(event.event_type)}
            </Badge>
            <span className="text-sm font-medium text-slate-950">{getEventDescription(event)}</span>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {getActorLabel(event.actor_type)}
            {event.actor_id ? ` · ${event.actor_id}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Clock3 className="h-3.5 w-3.5" />
          {formatCrmDateTime(event.created_at)}
        </div>
      </div>
      <details className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-slate-600">Показать payload</summary>
        <pre className="mt-2 overflow-auto text-[11px] leading-5 text-slate-600">
          {JSON.stringify(event.payload_json, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function hasRecentPriceRequest(events) {
  return (events || []).some((event) => event.event_type === "price_requested_by_client");
}

function CustomerOrderCard({
  order,
  detail,
  expanded,
  loadingDetail,
  detailError,
  onToggle,
  onEdit,
  onCopyPublicLink,
  onSendToClient,
  onPrint,
  onDownloadPdf,
  onCopyTo1c,
  onSendTo1c,
  onecSyncLoading,
  publicLinkLoading,
  sendToClientLoading,
  printLoading,
  pdfLoading,
  publicLinkUrl,
  onQuickStatusChange,
  quickStatusSaving,
  onAddToSupplierCart,
  supplierCartLoading,
  onRefreshPrices,
  refreshPricesLoading,
  token,
  canEdit,
  onOrderUpdated,
}) {
  const items = detail?.items || [];
  const events = [...(detail?.events || [])].reverse();
  const orderData = detail || order;
  const itemCount = orderData?.item_count ?? order.item_count ?? items.length;
  const statusValue = orderData?.status || order.status;
  const priceRequested = hasRecentPriceRequest(detail?.events);
  const needsManagerReview = statusValue === "partially_arrived";
  const canRefreshPrices =
    canEdit &&
    ["draft", "sent", "viewed", "awaiting_confirmation", "needs_revision"].includes(statusValue);

  return (
    <Card
      id={`customer-order-${order.id}`}
      className={`overflow-hidden bg-white shadow-sm ${
        needsManagerReview
          ? "border-amber-300 ring-1 ring-amber-200"
          : "border-slate-200"
      }`}
    >
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onToggle}
          className="block w-full text-left"
        >
          <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="truncate text-lg font-semibold tracking-tight text-slate-950">
                    {order.order_number}
                  </h3>
                  <Badge variant="outline" className={`rounded-full ${getOrderBadgeClass(statusValue)}`}>
                    {getCustomerOrderStatusLabel(statusValue)}
                  </Badge>
                  {orderData?.onec_sync_status ? (
                    <Badge
                      variant="outline"
                      className={`rounded-full ${
                        orderData.onec_sync_status === "synced"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : orderData.onec_sync_status === "failed"
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {getOnecSyncStatusLabel(orderData.onec_sync_status)}
                    </Badge>
                  ) : null}
                  <span className="text-xs text-slate-500">
                    <CalendarDays className="mr-1 inline-block h-3.5 w-3.5" />
                    {formatCrmDateTime(order.created_at)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {order.client_name_snapshot || "Без имени"} · {order.phone_snapshot || "Телефон не указан"}
                </p>
                {needsManagerReview && (
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Нужна ручная проверка и смена статуса
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 self-start">
                <div className="text-right">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Сумма</p>
                  <p className="text-xl font-semibold text-slate-950">
                    {formatMoney(order.total_amount, order.currency)}
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${
                    expanded ? "rotate-180" : ""
                  }`}
                />
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Клиент</p>
                <p className="mt-1 truncate text-sm font-medium text-slate-950">
                  {order.client_name_snapshot || "—"}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Телефон</p>
                <p className="mt-1 truncate text-sm font-medium text-slate-950">
                  {order.phone_snapshot || "—"}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Автомобиль</p>
                <p className="mt-1 truncate text-sm font-medium text-slate-950">
                  {getOrderVehicleLabel(order)}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Источник</p>
                <p className="mt-1 truncate text-sm font-medium text-slate-950">
                  {getCustomerOrderSourceLabel(order.source)}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Позиций</p>
                <p className="mt-1 text-sm font-medium text-slate-950">{itemCount}</p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Дата поставки</p>
                <p className="mt-1 text-sm font-medium text-slate-950">
                  {formatCrmDate(order.promised_delivery_date)}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Скидка</p>
                <p className="mt-1 text-sm font-medium text-slate-950">
                  {formatMoney(order.discount_amount, order.currency)}
                </p>
              </div>
              <div className={`rounded-xl px-3 py-2 ${needsManagerReview ? "bg-amber-50" : "bg-slate-50"}`}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Статус</p>
                <p className={`mt-1 text-sm font-medium ${needsManagerReview ? "text-amber-800" : "text-slate-950"}`}>
                  {getCustomerOrderStatusLabel(statusValue)}
                </p>
              </div>
            </div>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-5 sm:px-5">
            {needsManagerReview && (
              <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-semibold">Требуется проверка менеджера</p>
                    <p className="mt-1">
                      Часть товара не заказывалась у поставщика — она уже была в наличии. Проверьте
                      состав заказа, убедитесь что все позиции готовы, и вручную смените статус на
                      «Готов к выдаче» или другой подходящий.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {priceRequested && canRefreshPrices && (
              <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <p>
                    Клиент запросил актуальную цену. Обновите цены по прайсу и отправьте заказ повторно.
                  </p>
                </div>
                <Button
                  type="button"
                  className="w-full shrink-0 bg-amber-600 hover:bg-amber-700 sm:w-auto"
                  onClick={onRefreshPrices}
                  disabled={refreshPricesLoading}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {refreshPricesLoading ? "Обновляем..." : "Обновить цены по прайсу"}
                </Button>
              </div>
            )}
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                  Редактировать
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onCopyPublicLink} disabled={publicLinkLoading}>
                  <Link2 className="mr-2 h-4 w-4" />
                  {publicLinkLoading ? "Готовим ссылку..." : "Получить ссылку"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSendToClient}
                  disabled={sendToClientLoading}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {sendToClientLoading ? "Отправляем..." : "Отправить клиенту"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onPrint}
                  disabled={printLoading}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {printLoading ? "Готовим..." : "Печать"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDownloadPdf}
                  disabled={pdfLoading}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  {pdfLoading ? "Готовим..." : "PDF"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onCopyTo1c}
                  disabled={!items.length}
                  title="Скопировать для 1С"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  1С
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSendTo1c}
                  disabled={!items.length || onecSyncLoading}
                  title="Отправить заказ в 1С"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {onecSyncLoading ? "Отправка..." : "Отправить в 1С"}
                </Button>
                {canRefreshPrices && !priceRequested && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRefreshPrices}
                    disabled={refreshPricesLoading}
                  >
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    {refreshPricesLoading ? "Обновляем..." : "Обновить цены по прайсу"}
                  </Button>
                )}
                {publicLinkUrl ? (
                  <Button type="button" variant="ghost" size="sm" asChild>
                    <a href={publicLinkUrl} target="_blank" rel="noopener noreferrer">
                      Открыть публичную
                    </a>
                  </Button>
                ) : null}
                {["confirmed", "awaiting_payment", "in_work"].includes(statusValue) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onAddToSupplierCart}
                    disabled={supplierCartLoading}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {supplierCartLoading ? "Добавляем..." : "Добавить позиции в корзину поставщика"}
                  </Button>
                )}
              </div>
              <div className="min-w-[280px]">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Быстрая смена статуса
                </p>
                <Select value={statusValue} onValueChange={onQuickStatusChange} disabled={quickStatusSaving}>
                  <SelectTrigger className="mt-2 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_ORDER_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loadingDetail && <DetailLoading />}
            {!loadingDetail && detailError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {detailError}
              </div>
            )}
            {!loadingDetail && !detailError && detail && (
              <div className="space-y-6">
                <div>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Состав заказа
                    </h4>
                    <span className="text-xs text-slate-500">
                      Обновлено: {formatCrmDateTime(detail.updated_at)}
                    </span>
                  </div>

                  {items.length > 0 ? (
                    <Card className="border-slate-200 bg-white shadow-sm">
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                              <TableHead className="w-[140px]">Артикул</TableHead>
                              <TableHead>Наименование</TableHead>
                              <TableHead className="w-[110px] text-right">Кол-во</TableHead>
                              <TableHead className="w-[140px] text-right">Цена</TableHead>
                              <TableHead className="w-[100px] text-right">Скидка</TableHead>
                              <TableHead className="w-[150px] text-right">Сумма</TableHead>
                              <TableHead>Комментарий</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {items.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium text-slate-950">
                                  {item.product_code || "—"}
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium text-slate-950">{item.product_name}</div>
                                  {item.product_id && (
                                    <div className="mt-1 text-xs text-slate-500">ID: {item.product_id}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{item.quantity}</TableCell>
                                <TableCell className="text-right">
                                  {formatMoney(item.price, detail.currency)}
                                </TableCell>
                                <TableCell className="text-right">{item.discount_percent}%</TableCell>
                                <TableCell className="text-right font-semibold text-slate-950">
                                  {formatMoney(item.total, detail.currency)}
                                </TableCell>
                                <TableCell className="max-w-[280px] text-slate-600">
                                  <span className="block truncate">{item.comment || "—"}</span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500">
                      Позиции пока не добавлены.
                    </div>
                  )}
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      <CircleDollarSign className="h-4 w-4" />
                      Сумма без скидки
                    </div>
                    <p className="mt-3 text-2xl font-semibold text-slate-950">
                      {formatMoney(detail.subtotal_amount, detail.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      <Filter className="h-4 w-4" />
                      Скидка
                    </div>
                    <p className="mt-3 text-2xl font-semibold text-slate-950">
                      {formatMoney(detail.discount_amount, detail.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#3566f6]/20 bg-[#f5f8ff] p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#174bd7]">
                      <Package className="h-4 w-4" />
                      Итог к оплате
                    </div>
                    <p className="mt-3 text-2xl font-semibold text-[#0b1538]">
                      {formatMoney(detail.total_amount, detail.currency)}
                    </p>
                  </div>
                </div>

                <CustomerOrderPaymentsPanel
                  orderId={detail.id}
                  token={token}
                  canEdit={canEdit}
                  onOrderUpdated={onOrderUpdated}
                />

                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-slate-500" />
                    <h4 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
                      История событий
                    </h4>
                  </div>

                  {events.length > 0 ? (
                    <div className="space-y-3">
                      {events.map((event) => (
                        <EventCard key={event.id} event={event} />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-500">
                      Событий пока нет.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CrmCustomerOrdersPage() {
  const { user, token, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCountsMap, setStatusCountsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [detailErrors, setDetailErrors] = useState({});
  const [statusSaving, setStatusSaving] = useState({});
  const [publicLinkLoading, setPublicLinkLoading] = useState({});
  const [sendToClientLoading, setSendToClientLoading] = useState({});
  const [printLoading, setPrintLoading] = useState({});
  const [pdfLoading, setPdfLoading] = useState({});
  const [supplierCartLoading, setSupplierCartLoading] = useState({});
  const [refreshPricesLoading, setRefreshPricesLoading] = useState({});
  const [onecSyncLoading, setOnecSyncLoading] = useState({});
  const [publicLinkMap, setPublicLinkMap] = useState({});

  const q = searchParams.get("q") || "";
  const status = searchParams.get("status") || "";
  const source = searchParams.get("source") || "";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const pageSize = Math.max(1, Math.min(Number(searchParams.get("page_size") || 20), 200));
  const focusOrderId = Number(searchParams.get("order_id") || 0) || null;
  const focusHandledRef = useRef(null);
  const canEdit = userCanEditCrm(user);

  useEffect(() => {
    if (!focusOrderId) {
      focusHandledRef.current = null;
    }
  }, [focusOrderId]);

  useEffect(() => {
    if (!authLoading && !userHasCrmAccess(user)) {
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (status) params.status = status;
      if (source) params.source = source;
      if (q.trim()) params.search = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const data = await crmApi.listCustomerOrders(token, params);
      const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setOrders(items);
      setTotal(Array.isArray(data) ? items.length : Number(data?.total ?? items.length));
      setStatusCountsMap(data?.status_counts && typeof data.status_counts === "object" ? data.status_counts : {});
    } catch (err) {
      setError(extractCrmError(err, "Не удалось загрузить заказы покупателей"));
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, status, source, q, dateFrom, dateTo]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!focusOrderId || !token || focusHandledRef.current === focusOrderId) return;

    let cancelled = false;
    const orderId = focusOrderId;
    focusHandledRef.current = orderId;

    const openFocusedOrder = async () => {
      setExpandedIds((prev) => (prev.includes(orderId) ? prev : [...prev, orderId]));

      try {
        const detail = await crmApi.getCustomerOrder(token, orderId);
        if (cancelled) return;

        setDetailCache((prev) => ({ ...prev, [orderId]: detail }));
        setDetailErrors((prev) => ({ ...prev, [orderId]: null }));
        setOrders((prev) => {
          if (prev.some((item) => item.id === orderId)) {
            return prev.map((item) => (item.id === orderId ? { ...item, ...detail } : item));
          }
          return [detail, ...prev];
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(extractCrmError(err, "Не удалось открыть заказ"));
        }
        return;
      }

      const next = new URLSearchParams(searchParams);
      next.delete("order_id");
      setSearchParams(next, { replace: true });

      window.requestAnimationFrame(() => {
        document.getElementById(`customer-order-${orderId}`)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    };

    openFocusedOrder();

    return () => {
      cancelled = true;
    };
  }, [focusOrderId, token, searchParams, setSearchParams]);

  const statusCounts = useMemo(() => {
    return CUSTOMER_ORDER_STATUS_COUNTERS.map((item) => ({
      ...item,
      count: Number(statusCountsMap[item.value] || 0),
    }));
  }, [statusCountsMap]);

  const partiallyArrivedCount = Number(statusCountsMap.partially_arrived || 0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updateParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const mergeUpdatedOrder = useCallback((updated) => {
    if (!updated) return;
    setOrders((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              ...updated,
              item_count: updated.item_count ?? updated.items?.length ?? item.item_count,
            }
          : item
      )
    );
    setDetailCache((prev) => ({ ...prev, [updated.id]: updated }));
    setDetailErrors((prev) => ({ ...prev, [updated.id]: null }));
  }, []);

  const handleAddToSupplierCart = useCallback(
    async (order) => {
      if (!token || !order?.id) return;

      let orderData = detailCache[order.id];
      if (!orderData?.items) {
        setSupplierCartLoading((prev) => ({ ...prev, [order.id]: true }));
        try {
          orderData = await crmApi.getCustomerOrder(token, order.id);
          setDetailCache((prev) => ({ ...prev, [order.id]: orderData }));
        } catch (err) {
          toast.error(extractCrmError(err, "Не удалось загрузить состав заказа"));
          return;
        } finally {
          setSupplierCartLoading((prev) => ({ ...prev, [order.id]: false }));
        }
      }

      const cartItems = (orderData.items || [])
        .filter((item) => item.product_id)
        .map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          for_client: true,
          customer_order_number: orderData.order_number,
          customer_order_id: orderData.id,
          customer_order_item_id: item.id,
          client_id: orderData.client_id,
          client_name: orderData.client_name_snapshot || "",
          client_phone: orderData.phone_snapshot || "",
        }));

      if (!cartItems.length) {
        toast.error("Нет позиций с привязкой к каталогу — добавить в корзину поставщика нельзя");
        return;
      }

      setSupplierCartLoading((prev) => ({ ...prev, [order.id]: true }));
      try {
        const result = await batchAddToSupplierCart(token, cartItems);
        const added = Number(result?.success ?? 0);
        const failed = Number(result?.failed ?? 0);

        if (added < 1) {
          toast.error("Не удалось добавить позиции в корзину поставщика");
          return;
        }

        const updated = await crmApi.recordCustomerOrderSupplierCart(token, order.id, {
          item_ids: cartItems.map((item) => item.customer_order_item_id),
        });
        mergeUpdatedOrder(updated);

        if (failed > 0) {
          toast.warning(
            `Добавлено ${added} поз., не удалось ${failed}. Пометка: «Под клиента» · ${orderData.order_number}`
          );
        } else {
          toast.success(
            `Добавлено в корзину поставщика: ${added} поз. · «Под клиента» · ${orderData.order_number}`
          );
        }
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось добавить в корзину поставщика"));
      } finally {
        setSupplierCartLoading((prev) => ({ ...prev, [order.id]: false }));
      }
    },
    [token, detailCache, mergeUpdatedOrder]
  );

  const handleQuickStatusChange = useCallback(
    async (orderId, statusValue) => {
      const current = detailCache[orderId] || orders.find((item) => item.id === orderId);
      if (!current || current.status === statusValue) return;
      setStatusSaving((prev) => ({ ...prev, [orderId]: true }));
      try {
        const updated = await crmApi.changeCustomerOrderStatus(token, orderId, statusValue);
        mergeUpdatedOrder(updated);
        await fetchOrders();
        toast.success("Статус обновлён");
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось изменить статус"));
      } finally {
        setStatusSaving((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [detailCache, orders, token, mergeUpdatedOrder, fetchOrders]
  );

  const toAbsoluteUrl = useCallback((value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/")) return `${window.location.origin}${raw}`;
    return `${window.location.origin}/${raw}`;
  }, []);

  const handleCopyPublicLink = useCallback(
    async (orderId) => {
      if (!token) return;
      setPublicLinkLoading((prev) => ({ ...prev, [orderId]: true }));
      try {
        const payload = await crmApi.createCustomerOrderPublicLink(token, orderId);
        const publicUrl = toAbsoluteUrl(
          payload?.public_url || payload?.public_path || `/public/customer-orders/${payload?.public_token || ""}`
        );
        if (!publicUrl) {
          throw new Error("Сервер вернул пустую публичную ссылку");
        }
        setPublicLinkMap((prev) => ({ ...prev, [orderId]: publicUrl }));
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(publicUrl);
          toast.success("Публичная ссылка скопирована", { description: publicUrl });
        } else {
          toast.success("Публичная ссылка готова", { description: publicUrl });
        }
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось получить публичную ссылку"));
      } finally {
        setPublicLinkLoading((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [token, toAbsoluteUrl]
  );

  const handleSendToClient = useCallback(
    async (orderId) => {
      if (!token) return;
      setSendToClientLoading((prev) => ({ ...prev, [orderId]: true }));
      try {
        const res = await crmApi.sendCustomerOrderToClient(token, orderId);
        if (!res.success) {
          if (res.error === "send_disabled") {
            toast.error("Отправка WhatsApp отключена в настройках CRM");
          } else if (res.error === "missing_chat_id") {
            toast.error("У клиента не указан WhatsApp-чат");
          } else {
            toast.error(res.error || "Не удалось отправить сообщение");
          }
          return;
        }
        if (res.public_url) {
          setPublicLinkMap((prev) => ({ ...prev, [orderId]: toAbsoluteUrl(res.public_url) }));
        }
        toast.success("Ссылка на заказ отправлена клиенту в WhatsApp");
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось отправить заказ клиенту"));
      } finally {
        setSendToClientLoading((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [token, toAbsoluteUrl]
  );

  const handlePrintOrder = useCallback(
    async (order) => {
      if (!token || !order?.id) return;
      setPrintLoading((prev) => ({ ...prev, [order.id]: true }));
      try {
        await crmApi.openCustomerOrderPrint(token, order.id);
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось открыть печатную версию"));
      } finally {
        setPrintLoading((prev) => ({ ...prev, [order.id]: false }));
      }
    },
    [token]
  );

  const handleDownloadPdf = useCallback(
    async (order) => {
      if (!token || !order?.id) return;
      setPdfLoading((prev) => ({ ...prev, [order.id]: true }));
      try {
        await crmApi.downloadCustomerOrderPdf(token, order.id, order.order_number);
        toast.success("PDF сохранён");
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось скачать PDF"));
      } finally {
        setPdfLoading((prev) => ({ ...prev, [order.id]: false }));
      }
    },
    [token]
  );

  const handleCopyTo1c = useCallback(
    async (orderId) => {
      const detail = detailCache[orderId];
      const items = detail?.items || [];
      if (!items.length) {
        toast.warning("В заказе нет строк для копирования");
        return;
      }

      const text = buildCustomerOrder1cClipboard(items);
      if (!text.trim()) {
        toast.warning("Не удалось подготовить данные для 1С");
        return;
      }

      try {
        await copyTextToClipboard(text);
        toast.success("Данные для 1С скопированы");
      } catch {
        toast.error("Не удалось скопировать в буфер обмена");
      }
    },
    [detailCache]
  );

  const handleSendTo1c = useCallback(
    async (orderId) => {
      if (!token || !orderId) return;
      setOnecSyncLoading((prev) => ({ ...prev, [orderId]: true }));
      try {
        const result = await crmApi.onecSyncOrder(token, orderId);
        if (result?.skipped) {
          toast.info(result.reason === "already_synced" ? "Заказ уже отправлен в 1С" : "Синхронизация пропущена");
        } else {
          toast.success("Заказ отправлен в 1С");
        }
        const updated = await crmApi.getCustomerOrder(token, orderId);
        mergeUpdatedOrder(updated);
        await fetchOrders();
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось отправить заказ в 1С"));
      } finally {
        setOnecSyncLoading((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [token, mergeUpdatedOrder, fetchOrders]
  );

  const handleRefreshPrices = useCallback(
    async (order) => {
      if (!token || !order?.id) return;
      setRefreshPricesLoading((prev) => ({ ...prev, [order.id]: true }));
      try {
        const updated = await crmApi.refreshCustomerOrderPrices(token, order.id);
        mergeUpdatedOrder(updated);
        await fetchOrders();
        toast.success("Заказ обновлён по прайсу, срок предложения продлён");
      } catch (err) {
        toast.error(extractCrmError(err, "Не удалось обновить цены по прайсу"));
      } finally {
        setRefreshPricesLoading((prev) => ({ ...prev, [order.id]: false }));
      }
    },
    [token, mergeUpdatedOrder, fetchOrders]
  );

  const handleNewOrder = () => {
    navigate(`/crm/customer-orders/new${location.search}`);
  };

  const handleEditOrder = (orderId) => {
    navigate(`/crm/customer-orders/${orderId}/edit${location.search}`);
  };

  const closeForm = () => {
    navigate(`/crm/customer-orders${location.search}`);
  };

  const editRouteMatch = location.pathname.match(/^\/crm\/customer-orders\/(\d+)\/edit$/);
  const isNewRoute = location.pathname === "/crm/customer-orders/new";
  const formMode = isNewRoute ? "new" : editRouteMatch ? "edit" : null;
  const formOrderId = editRouteMatch ? Number(editRouteMatch[1]) : null;
  const initialClientId = isNewRoute ? searchParams.get("client_id") || null : null;
  const initialSource = isNewRoute ? searchParams.get("source") || null : null;
  const initialSourceMessageId = isNewRoute ? searchParams.get("message_id") || null : null;
  const initialSourceChatId = isNewRoute ? searchParams.get("chat_id") || null : null;

  const loadOrderDetail = useCallback(
    async (orderId) => {
      if (!token || detailCache[orderId] || detailLoading[orderId]) return;
      setDetailLoading((prev) => ({ ...prev, [orderId]: true }));
      setDetailErrors((prev) => ({ ...prev, [orderId]: null }));
      try {
        const detail = await crmApi.getCustomerOrder(token, orderId);
        setDetailCache((prev) => ({ ...prev, [orderId]: detail }));
      } catch (err) {
        setDetailErrors((prev) => ({
          ...prev,
          [orderId]: extractCrmError(err, "Не удалось загрузить заказ"),
        }));
      } finally {
        setDetailLoading((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [token, detailCache, detailLoading]
  );

  const toggleExpanded = async (orderId) => {
    setExpandedIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
    if (!detailCache[orderId]) {
      await loadOrderDetail(orderId);
    }
  };

  if (!authLoading && !userHasCrmAccess(user)) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="outline" className="mb-3 border-slate-200 bg-white text-slate-600">
            CRM журнал
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Заказы покупателей</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Клиентские заказы из WhatsApp и магазина
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={handleResetFilters} className="border-slate-200 bg-white">
            <RefreshCcw className="mr-2 h-4 w-4" />
            Сбросить фильтры
          </Button>
          <Button onClick={handleNewOrder} className="bg-[#3566f6] hover:bg-[#2f5be0]">
            <Plus className="mr-2 h-4 w-4" />
            Новый заказ
          </Button>
        </div>
      </div>

      {partiallyArrivedCount > 0 && status !== "partially_arrived" && (
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <p>
              <span className="font-semibold">
                {partiallyArrivedCount}{" "}
                {partiallyArrivedCount === 1 ? "заказ требует" : "заказов требуют"}
              </span>{" "}
              ручной проверки — часть товара была в наличии и не заказывалась у поставщика.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 border-amber-400 bg-white hover:bg-amber-100"
            onClick={() => updateParam("status", "partially_arrived")}
          >
            Показать заказы «Частично поступил»
          </Button>
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statusCounts.map((item) => (
          <CounterCard
            key={item.value}
            label={item.label}
            value={item.count}
            active={status === item.value}
            attention={CUSTOMER_ORDER_MANAGER_ATTENTION_STATUSES.includes(item.value)}
            onClick={() => updateParam("status", status === item.value ? "" : item.value)}
          />
        ))}
      </div>

      <Card className="mb-6 border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/60">
          <CardTitle className="flex items-center gap-2 text-xl text-slate-950">
            <Filter className="h-5 w-5 text-slate-500" />
            Фильтры
          </CardTitle>
          <CardDescription>
            Поиск по номеру заказа, клиенту, телефону, а также фильтрация по статусу, источнику и дате.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <FilterLabel>Поиск</FilterLabel>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => updateParam("q", e.target.value)}
                placeholder="Номер заказа, клиент, телефон"
                className="h-11 pl-9"
              />
            </div>
          </div>

          <div>
            <FilterLabel>Статус</FilterLabel>
            <Select value={status || "all"} onValueChange={(value) => updateParam("status", value === "all" ? "" : value)}>
              <SelectTrigger className="mt-2 h-11">
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {CUSTOMER_ORDER_STATUS_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <FilterLabel>Источник</FilterLabel>
            <Select value={source || "all"} onValueChange={(value) => updateParam("source", value === "all" ? "" : value)}>
              <SelectTrigger className="mt-2 h-11">
                <SelectValue placeholder="Все источники" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все источники</SelectItem>
                {CUSTOMER_ORDER_SOURCE_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <FilterLabel>Дата с</FilterLabel>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => updateParam("date_from", e.target.value)}
              className="mt-2 h-11"
            />
          </div>

          <div>
            <FilterLabel>Дата по</FilterLabel>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => updateParam("date_to", e.target.value)}
              className="mt-2 h-11"
            />
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="border-slate-200 bg-white shadow-sm">
              <CardContent className="space-y-4 p-5">
                <Skeleton className="h-6 w-64 rounded-full" />
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Skeleton className="h-16 rounded-xl" />
                  <Skeleton className="h-16 rounded-xl" />
                  <Skeleton className="h-16 rounded-xl" />
                  <Skeleton className="h-16 rounded-xl" />
                </div>
                <Skeleton className="h-12 w-full rounded-xl" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && error && (
        <Card className="border-red-200 bg-white shadow-sm">
          <CardContent className="flex items-start gap-3 p-5">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-slate-950">Не удалось загрузить заказы</h3>
              <p className="mt-1 text-sm text-slate-600">{error}</p>
            </div>
            <Button variant="outline" onClick={fetchOrders}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Повторить
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && orders.length === 0 && (
        <Card className="border-dashed border-slate-200 bg-white shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <Package className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-semibold text-slate-950">
              {total === 0 && !status && !source && !q && !dateFrom && !dateTo
                ? "Пока нет заказов покупателей."
                : "Заказов по этим фильтрам нет."}
            </h3>
            <p className="mt-2 max-w-xl text-sm text-slate-500">
              {total === 0 && !status && !source && !q && !dateFrom && !dateTo
                ? "После создания заказа он появится здесь."
                : "Попробуйте изменить фильтры или сбросить их, чтобы увидеть все заказы."}
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && orders.length > 0 && (
        <div className="space-y-4">
          {orders.map((order) => (
            <CustomerOrderCard
              key={order.id}
              order={order}
              detail={detailCache[order.id]}
              expanded={expandedIds.includes(order.id)}
              loadingDetail={!!detailLoading[order.id]}
              detailError={detailErrors[order.id]}
              onToggle={() => toggleExpanded(order.id)}
              onEdit={() => handleEditOrder(order.id)}
              onCopyPublicLink={() => handleCopyPublicLink(order.id)}
              onSendToClient={() => handleSendToClient(order.id)}
              onPrint={() => handlePrintOrder(order)}
              onDownloadPdf={() => handleDownloadPdf(order)}
              onCopyTo1c={() => handleCopyTo1c(order.id)}
              onSendTo1c={() => handleSendTo1c(order.id)}
              onecSyncLoading={!!onecSyncLoading[order.id]}
              publicLinkLoading={!!publicLinkLoading[order.id]}
              sendToClientLoading={!!sendToClientLoading[order.id]}
              printLoading={!!printLoading[order.id]}
              pdfLoading={!!pdfLoading[order.id]}
              publicLinkUrl={publicLinkMap[order.id] || ""}
              onQuickStatusChange={(statusValue) => handleQuickStatusChange(order.id, statusValue)}
              quickStatusSaving={!!statusSaving[order.id]}
              onAddToSupplierCart={() => handleAddToSupplierCart(order)}
              supplierCartLoading={!!supplierCartLoading[order.id]}
              onRefreshPrices={() => handleRefreshPrices(order)}
              refreshPricesLoading={!!refreshPricesLoading[order.id]}
              token={token}
              canEdit={canEdit}
              onOrderUpdated={mergeUpdatedOrder}
            />
          ))}
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Показано {orders.length} из {total} · страница {page} из {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => updateParam("page", String(page - 1))}
            >
              Назад
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => updateParam("page", String(page + 1))}
            >
              Вперёд
            </Button>
          </div>
        </div>
      )}

      {formMode && (
        <CustomerOrderFormDialog
          open
          mode={formMode}
          orderId={formOrderId}
          initialClientId={initialClientId}
          initialSource={initialSource}
          initialSourceMessageId={initialSourceMessageId}
          initialSourceChatId={initialSourceChatId}
          onClose={closeForm}
          onSaved={async () => {
            await fetchOrders();
          }}
        />
      )}
    </div>
  );
}
